import { useState, useEffect } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/AdminLayout';
import axios from 'axios';
import {
  Users, ShoppingCart, Zap, AlertTriangle,
  Bell, Clock, TrendingUp, Server
} from 'lucide-react';

function StatCard({ icon: Icon, label, value, sub, color, testId }) {
  return (
    <div className="bg-[#0d1224] border border-slate-800/60 rounded-xl p-4" data-testid={testId}>
      <div className="flex items-start justify-between mb-3">
        <div className={`w-9 h-9 rounded-lg ${color} flex items-center justify-center`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
      </div>
      <p className="text-2xl font-bold text-white mb-0.5">{value}</p>
      <p className="text-xs text-slate-400">{label}</p>
      {sub && <p className="text-[10px] text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

export default function AdminDashboardPage() {
  const { authHeaders, API } = useAdminAuth();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/overview`, authHeaders())
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [API, authHeaders]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  const d = data || {};
  const tenants = d.tenants || {};
  const errors = d.errors || {};
  const hourly = d.hourly_requests || [];
  const maxReq = Math.max(...hourly.map(h => h.requests), 1);

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-dashboard">
        <div>
          <h1 className="text-xl font-bold text-white">{t('admin.overview.title')}</h1>
          <p className="text-sm text-slate-500 mt-1">{t('admin.overview.subtitle')}</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard
            icon={Users} label={t('admin.overview.totalTenants')} value={tenants.total || 0}
            sub={`${tenants.active || 0} ${t('admin.overview.activeThisWeek')}`}
            color="bg-blue-500/20" testId="stat-tenants"
          />
          <StatCard
            icon={ShoppingCart} label={t('admin.overview.ordersToday')} value={d.orders_today || 0}
            color="bg-emerald-500/20" testId="stat-orders"
          />
          <StatCard
            icon={Zap} label={t('admin.overview.requestsToday')} value={d.requests_today || 0}
            sub={`${t('admin.overview.avgMs')} ${d.avg_response_time_ms || 0}ms`}
            color="bg-amber-500/20" testId="stat-requests"
          />
          <StatCard
            icon={AlertTriangle} label={t('admin.overview.errorsToday')} value={errors.today || 0}
            sub={errors.critical ? `${errors.critical} ${t('admin.overview.critical')}` : t('admin.overview.allClear')}
            color={errors.critical > 0 ? "bg-red-500/20" : "bg-slate-500/20"} testId="stat-errors"
          />
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard
            icon={TrendingUp} label={t('admin.overview.newTenantsWeek')} value={tenants.new_this_week || 0}
            color="bg-indigo-500/20" testId="stat-new-tenants"
          />
          <StatCard
            icon={Server} label={t('admin.overview.avgResponse')} value={`${d.avg_response_time_ms || 0}ms`}
            color="bg-cyan-500/20" testId="stat-avg-response"
          />
          <StatCard
            icon={Bell} label={t('admin.overview.unreadAlerts')} value={d.unread_alerts || 0}
            color={d.unread_alerts > 0 ? "bg-orange-500/20" : "bg-slate-500/20"} testId="stat-unread-alerts"
          />
          <StatCard
            icon={Clock} label={t('admin.overview.activeTenants')} value={tenants.active || 0}
            sub={t('admin.overview.last7Days')}
            color="bg-purple-500/20" testId="stat-active-tenants"
          />
        </div>

        <div className="bg-[#0d1224] border border-slate-800/60 rounded-xl p-5" data-testid="hourly-chart">
          <h2 className="text-sm font-semibold text-white mb-4">{t('admin.overview.requestTraffic')}</h2>
          <div className="flex items-end gap-1 h-32">
            {hourly.map((h, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div
                  className="w-full bg-red-500/30 rounded-t transition-all hover:bg-red-500/50"
                  style={{ height: `${Math.max((h.requests / maxReq) * 100, 2)}%` }}
                  title={`${h.hour}: ${h.requests}`}
                />
                {i % 4 === 0 && (
                  <span className="text-[8px] text-slate-600">{h.hour}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
